import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"

import { formatDateDataGrid } from "src/utils/format-date"
import { DATE_FORMAT } from "src/utils/constants"

import DatalakeService from "src/services/datalake-service"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"

const ParameterDatalakeCVMObrigacoesPeriodicasTable = (props) => {
    const dataLakeService = new DatalakeService()

    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({
        count: 0,
        next: null,
        previous: null
    })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)

        dataLakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body

            const list = results.map((obj, index) => ({
                ...obj,
                id: index
            }))

            setPagination({
                count,
                next,
                previous
            })

            setData(list)
        })
    }

    const createRow = async () => {
        const body = {
            CPFCNPJ: "N/A",
            DataReferencia: dayjs().format(DATE_FORMAT),
            Versao: 1,
            CodigoCVM: 0,
            CategoriaDocumento: "N/A",
            TipoDocumento: "N/A",
            EspecieDocumento: "N/A",
            AssuntoDocumento: "N/A",
            DataRecebimento: dayjs().format(DATE_FORMAT),
            LinkDocumento: "N/A"
        }

        props.loading(true)

        return dataLakeService
            .createTableEndpoint(props.endpoint, body)
            .then(() => {
                props.loading(false)
                loadData()
            })
            .catch((err) => {
                props.loading(false)
                throw err
            })
    }

    const updateRow = async (newRow) => {
        const {
            ID,
            CPFCNPJ,
            DataReferencia,
            Versao,
            CodigoCVM,
            CategoriaDocumento,
            TipoDocumento,
            EspecieDocumento,
            AssuntoDocumento,
            DataRecebimento,
            LinkDocumento
        } = newRow

        const body = {
            CPFCNPJ,
            DataReferencia,
            Versao,
            CodigoCVM,
            CategoriaDocumento,
            TipoDocumento,
            EspecieDocumento,
            AssuntoDocumento,
            DataRecebimento,
            LinkDocumento
        }

        props.loading(true)

        return dataLakeService
            .updateTableEndpoint(props.endpoint, ID, body)
            .then(() => {
                props.loading(false)
            })
            .catch((err) => {
                props.loading(false)
                throw err
            })
    }

    const deleteRow = async (row) => {
        props.loading(true)

        const { ID } = row

        return dataLakeService
            .deleteTableEndpoint(props.endpoint, ID)
            .then(() => {
                props.loading(false)

                const listRemoved = data.filter((item) => item.ID !== ID)
                setData(listRemoved)
            })
            .catch((err) => {
                props.loading(false)
                throw err
            })
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList
            .map((obj) => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`)
            .join("&")

        props.loading(true)

        dataLakeService
            .loadPaginationEndpoint(props.endpoint, query, limit, offset)
            .then((response) => {
                props.loading(false)

                const { body } = response
                const { results, count, next, previous } = body

                const list = results.map((obj) => ({
                    ...obj,
                    id: obj.ID
                }))

                setPagination({
                    count,
                    next,
                    previous
                })

                setData(list)
            })
    }

    const changePage = async (endpoint) => {
        props.loading(true)

        dataLakeService.paginate(endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body

            const list = results.map((obj) => ({
                ...obj,
                id: obj.ID
            }))

            setPagination({
                count,
                next,
                previous
            })

            setData([])
            setData(list)
        })
    }

    const columns = [
        {
            field: "ID",
            headerName: "#",
            disableColumnMenu: true,
            disableFilter: true,
            flex: true,
            minWidth: 80
        },
        {
            field: "CPFCNPJ",
            headerName: "CPF/CNPJ",
            flex: 1,
            minWidth: 150,
            maxWidth: 200,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: "DataReferencia",
            headerName: "Data Referência",
            flex: 1,
            minWidth: 150,
            maxWidth: 200,
            typeComponent: "date",
            disableColumnMenu: true,
            editable: true
        },
        {
            field: "Versao",
            headerName: "Versão",
            flex: 1,
            minWidth: 100,
            maxWidth: 120,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: "CodigoCVM",
            headerName: "Código CVM",
            flex: 1,
            minWidth: 120,
            maxWidth: 150,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: "CategoriaDocumento",
            headerName: "Categoria Documento",
            flex: 1,
            minWidth: 200,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: "TipoDocumento",
            headerName: "Tipo Documento",
            flex: 1,
            minWidth: 220,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: "EspecieDocumento",
            headerName: "Espécie Documento",
            flex: 1,
            minWidth: 220,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: "AssuntoDocumento",
            headerName: "Assunto Documento",
            flex: 1,
            minWidth: 300,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: "DataRecebimento",
            headerName: "Data Recebimento",
            flex: 1,
            minWidth: 170,
            typeComponent: "date",
            disableColumnMenu: true,
            editable: true
        },
        {
            field: "LinkDocumento",
            headerName: "Link Documento",
            flex: 1,
            minWidth: 250,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: "CriadoEm",
            headerName: "Data de Criação",
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: "AtualizadoEm",
            headerName: "Data de Atualização",
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                deleteRow={deleteRow}
                updateRow={updateRow}
                filterFunction={onFilter}
                pagination={pagination}
                onChangePage={changePage}
                customFilter={true}
            />
        </div>
    )
}

ParameterDatalakeCVMObrigacoesPeriodicasTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string
}

export default ParameterDatalakeCVMObrigacoesPeriodicasTable